import { getChunkContainerByChunkId } from '../../foreground/chunkElement';
// eslint-disable-next-line import/no-cycle
import {
  chunkContainerHasContent,
  createHighlightAtTtsPosition,
  forceChunkContentLoadAtPosition,
  forceContentLoadForContainer,
  forceContentUnloadForContainer,
  getAllWebviewDocumentChunks,
  getChunkContainerElements,
  getChunkIdFromSerializedPosition,
  getPositionForTtsFromCurrentScrollPosition,
  getRectsFromTtsPosition,
  getSurroundingChunkPromisesToLoadAndUnload,
  populateOrRepopulateTTSAbleElements,
  scrollToTtsPosition,
} from '../../foreground/contentFramePortalGateInternalMethods';
import eventEmitter from '../../foreground/contentFramePortalGateInternalMethods/eventEmitter';
import { ScrollingManagerCore } from '../../foreground/contentFramePortalGateInternalMethods/ScrollingManagerCore';
import {
  calculateRelativeChunkScrollDepthFromTotalScrollDepth,
} from '../../foreground/scrollDepth';
import { getFocusableElementFromSerializedPosition } from '../../foreground/serializedPosition';
import type { ChunkContainerElement } from '../../foreground/types/chunkedDocuments';
import { InitialScrollPositionData } from '../../foreground/types/contentFramePuppeteerRelated';
import { ChunkScrollPosition } from '../../foreground/types/events';
import { getHeadingElement, getScrollTargetForInternalLink } from '../../foreground/utils/chunkedScrolling';
import {
  getVisibleElementsWithinRectBounds,
  isIntersecting,
} from '../../foreground/utils/findCenteredElementInViewport';
import { findChunkContainerForNode } from '../../foreground/utils/findChunkContainerForNode';
import getRangyClassApplier from '../../foreground/utils/getRangyClassApplier';
import { isNodeAnHTMLElement } from '../../foreground/utils/isNodeAnHTMLElement';
import {
  deserializeCanonicalRange,
  serializePositionAsCanonical,
} from '../../foreground/utils/locationSerialization/chunked';
import { makeWebviewLogger } from '../../foreground/utils/makeWebviewLogger';
import type { EpubLandmark, EpubPageListItem, LenientReadingPosition, TtsPosition, WordBoundary } from '../../types';
import { isContentRootWithMultipleChunks, isHTMLElement, isTextNode } from '../../typeValidators';
import nowTimestamp from '../../utils/dates/nowTimestamp';
import exceptionHandler from '../../utils/exceptionHandler.platform';
import makeLogger from '../../utils/makeLogger';
import { ScrollingManagerError } from './errors';
import type { MobileContentFrameWindow } from './types';

declare let window: MobileContentFrameWindow;

const IGNORED_DOCUMENT_CHILD_NODE_TAGS = new Set(['STYLE']);

/**
 * Checks if a page number is valid (roman numeral or integer).
 */
function isValidPageNumber(pageNumber: string | null | undefined): boolean {
  if (typeof pageNumber !== 'string') {
    return false;
  }
  // Integer check (e.g., "1", "23", "100")
  if (/^\d+$/.test(pageNumber)) {
    return true;
  }
  // Roman numeral check (case insensitive, e.g., "i", "iv", "ix", "xii")
  if (/^[ivxlcdm]+$/i.test(pageNumber)) {
    return true;
  }
  return false;
}

function preventDefault(e: TouchEvent) {
  e.preventDefault();
}

type CenteredElementInfo = { element?: HTMLElement | undefined | null; scrollDelta?: number; };

export type ScrollListenerEvent = {
  currentPage?: number;
  currentChunkElement: ChunkContainerElement;
  currentCenterElementInfo: CenteredElementInfo;
};

export type ScrollListenerFunction = ({ currentPage, currentChunkElement }: ScrollListenerEvent) => void;

export type SerializedPositionInfo = {
  serializedPosition: string;
  serializedPositionElementOffset: number;
};

export type PageRect = {
  // The top coordinate of the page (relative to entire document)
  top: number;
  // The bottom coordinate of the page (relative to entire document)
  bottom: number;
  // The height of the divider that sits at the bottom of the page
  bottomPageDividerHeight: number;
};

type ScrollingManagerEventListener = {
  target: HTMLElement | Document | MobileContentFrameWindow;
  eventName: string;
  callback: (args: unknown) => void;
};

// what are the different reasons we might disable or enable scroll
export enum TouchEventDisabledSource {
  Init = 'init',
  Scroll = 'scroll',
  ChunkLoading = 'chunkLoading',
  ChunkRendering = 'chunkRendering',
}


const logger = makeWebviewLogger(__filename);

export abstract class ScrollingManager implements ScrollingManagerCore {
  // This is a base class for all things scrolling
  document = document;
  isOnResizeDisabled = false;
  ttsAutoScrollingEnabled = false;
  focusableElements: HTMLElement[] = [];
  readingPosition: LenientReadingPosition | null = null;
  wordBoundaries: WordBoundary[] = [];
  isScrollingDown = false;
  currentScrollValue = 0;
  previousScrollValue = 0;
  scrollTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  scrollingEnabled = true;
  scrollEventsDisabledTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  touchMoveThrottle = 0;
  scrollingEventsDisabled = true;
  documentTextContentHeight = 0;
  pageHeight = 800;
  currentChunkIndex = 0;
  bottomMargin = 40;
  topMargin = 40;

  scrollTimeForPage = 200;
  iosScrollDelay = 300;
  hapticsOnScrollTimeModifier = 20;

  hapticsOnScrollEnabled = false;
  smoothAnimationsDisabled = false;
  scrollListeners: ScrollListenerFunction[] = [];
  eventListeners: ScrollingManagerEventListener[] = [];
  initialized = false;
  currentCenteredElementInfo: CenteredElementInfo = {};
  bodyObserver: ResizeObserver | undefined;
  baseLineHeight = 28;
  window: MobileContentFrameWindow = window;
  leftClickAreaWidth: number;
  rightClickAreaWidth: number;
  spinnerTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  chunksLoading = 0;
  boundOnContentChanged: typeof this.onContentChanged | undefined;
  fpsStart = 0;
  fpsTrackingStartTime = 0;
  fpsCounter = 0;
  shouldLogFPS = false;
  hasFPSReachedThreshold = false;
  logger = makeWebviewLogger(__filename);
  debugFreeScroll = false;
  enableScrollTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  touchEventsDisabledSources = new Set<TouchEventDisabledSource>([]);
  landmarks: EpubLandmark[] | undefined = undefined;
  pageList: EpubPageListItem[] | undefined = undefined;
  _cachedStartChunkId: string | undefined = undefined;
  _cachedEndChunkId: string | undefined = undefined;
  _debugInitialScrollPosition: InitialScrollPositionData | null = null;

  _internalDocumentTextRanges: Range[] | undefined = undefined;


  constructor(window: MobileContentFrameWindow) {
    this.window = window;
    const deviceWidth = this.window.innerWidth;
    this.leftClickAreaWidth = Math.min(deviceWidth * 0.2, 120);
    this.rightClickAreaWidth = Math.max(deviceWidth * 0.8, deviceWidth - 120);
    this.setBottomMargin(0);
    this.setTopMargin(0);
  }

  addEventListener(
    target: HTMLElement | Document | MobileContentFrameWindow,
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (args: any) => void,
  ) {
    target.addEventListener(eventName, callback);
    this.eventListeners.push({ target, eventName, callback });
  }

  removeAllEventListeners() {
    for (const eventObject of this.eventListeners) {
      const { target, eventName, callback } = eventObject;
      target.removeEventListener(eventName, callback);
    }
  }

  disableAllTouchEvents(source: TouchEventDisabledSource) {
    this.touchEventsDisabledSources.add(source);
    this._disableScrollingWithTouch();
  }

  enableAllTouchEvents(source: TouchEventDisabledSource) {
    this.touchEventsDisabledSources.delete(source);
    if (this.touchEventsDisabledSources.size === 0) {
      this._enableScrollingWithTouch();
    }
  }

  getScrollingElement() {
    if (this.document.scrollingElement === null) {
      throw new ScrollingManagerError('ScrollingElement is null!');
    }
    // claude eslint: scrollingElement null is checked above; in this document context it is always an HTMLElement
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return this.document.scrollingElement as HTMLElement;
  }

  getScrollingElementTop() {
    return this.getScrollingElement().scrollTop;
  }

  getScrollingElementMaxScroll() {
    const { scrollHeight } = this.getScrollingElement();
    return scrollHeight;
  }

  getAbsoluteScrollTopOfRange(element: HTMLElement | Element | Range) {
    return this.getScrollingElementTop() + element.getBoundingClientRect().top;
  }

  getAbsoluteClientRectOfElement(element: HTMLElement | Element | Range): DOMRect {
    const clientRect = element.getBoundingClientRect();
    const scrollTop = this.getScrollingElementTop();
    return {
      ...clientRect,
      height: clientRect.height,
      width: clientRect.width,
      left: clientRect.left,
      right: clientRect.right,
      top: scrollTop + clientRect.top,
      bottom: scrollTop + clientRect.bottom,
    };
  }

  getChunkContainerByIndex(index: number): ChunkContainerElement {
    const chunk = this.document.querySelector(`[data-chunk-index="${index}"]`);
    if (!chunk) {
      throw new ScrollingManagerError(`getChunkContainerByIndex no chunk with index ${index} found`);
    }
    // claude eslint: querySelector returns Element; elements with data-chunk-index are always ChunkContainerElements
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return chunk as ChunkContainerElement;
  }

  /**
   * Returns the reading progress from the current serialized position between 0 and 1
   */
  async getReadingPositionData(): Promise<ChunkScrollPosition | undefined> {
    if (!this.readingPosition) {
      return;
    }
    let readingPositionElement: HTMLElement | undefined;
    // Need to find a way to reverse engineer chunk container from absolute scroll depth
    let chunkContainerForReadingPosition = null;
    if (this.readingPosition.serializedPosition) {
      chunkContainerForReadingPosition = await this.loadChunkContentAtSerializedPosition(this.readingPosition.serializedPosition);
    } else if (this.readingPosition.scrollDepth !== null) {
      chunkContainerForReadingPosition = await this.loadChunkContentAtAbsoluteScrollDepth(this.readingPosition.scrollDepth);
    }
    if (!chunkContainerForReadingPosition) {
      return;
    }

    if (this.readingPosition?.serializedPosition) {
      readingPositionElement = await this.getFocusableElementFromSerializedPosition(
        this.readingPosition?.serializedPosition,
      );
    }

    return {
      scrollDepthWithinChunk: this.getChunkScrollDepth(chunkContainerForReadingPosition, readingPositionElement),
      chunkIndex: Number(chunkContainerForReadingPosition.dataset.chunkIndex),
      chunkId: chunkContainerForReadingPosition.dataset.chunkId,
      serializedPosition: this.readingPosition.serializedPosition,
      serializedElementVerticalOffset: this.readingPosition.mobileSerializedPositionElementVerticalOffset,
    };
  }

  get isChunked() {
    if (!this.documentRoot) {
      return false;
    }
    return isContentRootWithMultipleChunks(this.documentRoot);
  }

  setTopMargin(margin: number) {
    this.topMargin = Math.max(20, margin + (this.window.safeAreaMargins?.top ?? 0));
  }

  setBottomMargin(margin: number) {
    this.bottomMargin = Math.max(20, margin + (this.window.safeAreaMargins?.bottom ?? 0));
  }

  setScrollingElementTop(newTop: number) {
    this.getScrollingElement().scrollTop = newTop;
  }

  scrollingElementScrollTo({ top, behavior }: { top: number; behavior: 'smooth' | 'auto' | 'instant'; }) {
    this.getScrollingElement().scrollTo({ top, behavior });
  }

  init() {
    if (this.window.isScrollingManagerFPSLoggingEnabled) {
      this.logFPS();
    }
    this.disableScrollEvents();
    if (this.initialized) {
      throw new ScrollingManagerError(
        'ScrollingManager already initialized; make sure to not call init twice!',
      );
    }
    // Register functions for window so React Native can use them
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('ScrollingManagerInit documentTextContentContainer not found!');
    }
    if (!this.documentRoot) {
      throw new ScrollingManagerError('No documentRoot element found');
    }
    const newHeight = this.documentTextContent?.getBoundingClientRect().height;
    if (newHeight) {
      this.documentTextContentHeight = newHeight;
    }
    this.createResizeObserver();
    this.boundOnContentChanged = this.onContentChanged.bind(this);
    eventEmitter.on('chunk-content-unloaded', this.boundOnContentChanged);
    eventEmitter.on('chunk-content-loaded', this.boundOnContentChanged);
    this.onContentChanged();
  }

  createResizeObserver() {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('ScrollingManagerInit documentTextContentContainer not found!');
    }
    if (this.bodyObserver) {
      return;
    }
    this.bodyObserver = new ResizeObserver(() => this.onResize(false));
    this.bodyObserver.observe(this.documentTextContent);
  }


  destroy() {
    this.scrollListeners = [];
    this.destroyResizeObserver();
    if (window && window.getSelection()) {
      window?.getSelection()?.removeAllRanges();
    }
    this.enableAllPaginationElements();
    if (this.boundOnContentChanged) {
      eventEmitter.off('chunk-content-unloaded', this.boundOnContentChanged);
      eventEmitter.off('chunk-content-loaded', this.boundOnContentChanged);
    }

    // Reset memoized chunk IDs
    this._cachedStartChunkId = undefined;
    this._cachedEndChunkId = undefined;

    this.setSpinnerEnabled(false);
    this._enableScrollingWithTouch();
    this.document.body.classList.remove('disable-scroll');
  }

  destroyResizeObserver() {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('ScrollingManagerInit documentTextContentContainer not found!');
    }
    if (!this.bodyObserver) {
      return;
    }
    this.bodyObserver.unobserve(this.documentTextContent);
    delete this.bodyObserver;
  }

  get windowWidth() {
    return this.document.body.getBoundingClientRect().width;
  }

  onResize(force = false) {
    // This event is handled inside ResizeObserver which handles errors a bit annoyingly
    // eslint-disable-next-line no-alert
    alert(`HandleResize must be implemented in child class, ${force}`);
  }

  addScrollListener(func: ScrollListenerFunction) {
    this.scrollListeners.push(func);
  }

  disableScrollEventsForNMilliseconds(milliseconds = 1000) {
    this.scrollingEventsDisabled = true;
    if (this.scrollEventsDisabledTimer) {
      clearTimeout(this.scrollEventsDisabledTimer);
      this.scrollEventsDisabledTimer = undefined;
    }
    if (milliseconds > 0) {
      this.scrollEventsDisabledTimer = setTimeout(() => {
        this.scrollingEventsDisabled = false;
      }, milliseconds);
    } else {
      this.scrollingEventsDisabled = false;
    }
  }

  abstract enableAllPaginationElements(): void;

  abstract disableAllPaginationElements(): void;

  abstract updateCurrentCenteredElement(): void;

  abstract handleChunksLoading(): void;

  abstract setNewChunkIndex(newIndex: number, pageRectsToCompute?: number): Promise<void>;

  abstract instantlyScrollPageUp(): void;
  abstract instantlyScrollPageDown(): void;

  abstract getChunkScrollDepth(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement | Range | DOMRect): number;

  disableScrollEvents() {
    this.scrollingEventsDisabled = true;
  }

  enableScrollEvents() {
    if (this.scrollEventsDisabledTimer) {
      clearTimeout(this.scrollEventsDisabledTimer);
      this.scrollEventsDisabledTimer = undefined;
    }
    this.scrollingEventsDisabled = false;
  }

  async scrollToReadingPosition(readingPosition: LenientReadingPosition) {
    if (readingPosition.serializedPosition) {
      await forceChunkContentLoadAtPosition(readingPosition.serializedPosition);
      try {
        await this.scrollToSerializedPosition(
          readingPosition.serializedPosition,
          readingPosition.mobileSerializedPositionElementVerticalOffset ?? 0,
        );
      } catch (e) {
        exceptionHandler.captureException(e);
        // Fallback to scroll depth
        if (readingPosition.scrollDepth) {
          return this.scrollToReadingPositionScrollDepth(readingPosition.scrollDepth);
        }
        await this.scrollToStartOfDocument();
      }
    } else if (readingPosition.scrollDepth) {
      return this.scrollToReadingPositionScrollDepth(readingPosition.scrollDepth);
    } else {
      await this.scrollToStartOfDocument();
    }
    this.updateCurrentCenteredElement();
    this.enableAllTouchEvents(TouchEventDisabledSource.Scroll);
  }

  findChunkContainerForElement(element: HTMLElement | Node) {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError(
        'ScrollToSerializedPosition no document text content container found',
      );
    }
    return findChunkContainerForNode(element, this.documentTextContent);
  }


  async loadChunkContentAtSerializedPosition(serializedPosition: string): Promise<ChunkContainerElement | undefined> {
    const chunkId = getChunkIdFromSerializedPosition(serializedPosition);
    if (!chunkId) {
      return;
    }
    const chunkContainer = getChunkContainerByChunkId(chunkId);
    if (!chunkContainer) {
      return;
    }
    if (chunkContainerHasContent(chunkContainer)) {
      return chunkContainer;
    }
    this.incrementChunksLoading();
    await forceChunkContentLoadAtPosition(serializedPosition);
    // console.log('Chunk forced to load at ', serializedPosition);
    this.decrementChunksLoading();
    return chunkContainer;
  }


  async loadChunkContentAtAbsoluteScrollDepth(totalScrollDepthOfDocument: number): Promise<ChunkContainerElement | undefined> {
    const allWebviewChunks = await getAllWebviewDocumentChunks();
    if (!allWebviewChunks) {
      return;
    }

    const {
      chunkIndex,
    } = calculateRelativeChunkScrollDepthFromTotalScrollDepth(allWebviewChunks, totalScrollDepthOfDocument);
    const chunkContainer = this.getChunkContainerByIndex(chunkIndex);
    await this.loadChunkContentForContainer(chunkContainer);
    return chunkContainer;
  }

  incrementChunksLoading() {
    this.chunksLoading += 1;
    this.handleChunksLoading();
  }

  decrementChunksLoading() {
    this.chunksLoading = Math.max(this.chunksLoading - 1, 0);
    this.handleChunksLoading();
  }


  async loadChunkContentForContainer(chunkContainer: ChunkContainerElement) {
    if (chunkContainerHasContent(chunkContainer)) {
      return;
    }
    this.incrementChunksLoading();
    await forceContentLoadForContainer(chunkContainer);
    this.decrementChunksLoading();
  }

  async unloadChunkContentForContainer(chunkContainer: ChunkContainerElement) {
    this.incrementChunksLoading();
    await forceContentUnloadForContainer(chunkContainer);
    this.decrementChunksLoading();
  }

  async scrollToSerializedPosition(serializedPosition: string, offset: number, behavior: 'smooth' | 'auto' | 'instant' = 'auto') {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError(
        'ScrollToSerializedPosition no document text content container found',
      );
    }

    await this.loadChunkContentAtSerializedPosition(serializedPosition);
    const target = await this.getFocusableElementFromSerializedPosition(serializedPosition);
    if (!target) {
      throw new ScrollingManagerError(
        `ScrollToSerializedPosition no target found for serialized position ${serializedPosition}`,
      );
    }
    this.currentCenteredElementInfo = { element: target, scrollDelta: offset };
    // console.log('Time to scroll to element ', target, offset);
    await this._scrollToRangeOrElement(target, offset, behavior);
  }

  async scrollToSerializedRange(serializedRange: string, offset: number) {
    const firstPositionInOccurrence = serializedRange.split(',')[0];
    if (!this.documentTextContent) {
      throw new ScrollingManagerError(
        'ScrollToSerializedPosition no document text content container found',
      );
    }

    try {
      // TODO: Perhaps we want to load the chunk which contains the last occurrence too
      await this.loadChunkContentAtSerializedPosition(firstPositionInOccurrence);
      const range = deserializeCanonicalRange(
        serializedRange,
        this.documentTextContent,
        this.document,
        getRangyClassApplier(),
      ).nativeRange;
      if (!range) {
        throw new ScrollingManagerError(
          `scrollToSerializedRange no range found for serialized range ${serializedRange}`,
        );
      }

      this._scrollToRangeOrElement(range, offset, 'auto');
    } catch (e) {
      exceptionHandler.captureException(e);
    }
  }

  get headerImageContainer() {
    return this.document.getElementById('header-image-container');
  }

  get headerContainer() {
    return this.document.querySelector<HTMLElement>('.header-container');
  }

  get bottomMarginElement() {
    return this.document.querySelector<HTMLElement>('.bottom-margin');
  }

  get documentTextContent() {
    return this.document.getElementById('document-text-content');
  }

  get documentRoot() {
    return this.document.querySelector<HTMLElement>('.document-root');
  }

  get bottomPageSnapshotElement() {
    return this.document.querySelector<HTMLElement>(
      '.absolutely-positioned-content.bottom',
    );
  }

  get topPageSnapshotElement() {
    return this.document.querySelector<HTMLElement>(
      '.absolutely-positioned-content.top',
    );
  }

  handleScrollFromHref() {
  }

  refreshPageSnapshotsForCurrentPage() {
    // Only implemented in paginated scrolling manager
  }

  setReadingPosition(pos: LenientReadingPosition | null) {
    this.readingPosition = pos;
  }

  updateWordBoundaries(wordBoundaries: WordBoundary[]) {
    this.wordBoundaries = wordBoundaries;
  }

  toggleTTSAutoScrolling(enabled: boolean) {
    this.ttsAutoScrollingEnabled = enabled;
  }

  abstract isDocumentScrolledToBeginning(): boolean;

  async getFocusableElementFromSerializedPosition(serializedPosition: string): Promise<HTMLElement | undefined> {
    await this.loadChunkContentAtSerializedPosition(serializedPosition);
    return getFocusableElementFromSerializedPosition(serializedPosition, this.documentTextContent, true);
  }

  async playTtsFromCurrentScrollPosition() {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('Document element not found');
    }
    this.ttsAutoScrollingEnabled = false;

    const positionResult = await getPositionForTtsFromCurrentScrollPosition({
      getIsDocumentScrolledToTop: () => this.isDocumentScrolledToBeginning(),
      window: this.window,
    });

    if (!positionResult) {
      return;
    }

    this.ttsAutoScrollingEnabled = true;
    const { elementIndex, position, chunkId: currentChunkId } = positionResult;

    if (typeof elementIndex !== 'undefined') {
      // if we didn't find an accurate timestamp from word boundaries
      this.window.portalGateToForeground.emit('play-tts-from-element', {
        elementIndex,
        position,
        chunkId: currentChunkId,
      });
      return;
    }
    // if we got an accurate timestamp
    this.window.portalGateToForeground.emit('play-tts-from-timestamp', {
      timestamp: position,
      chunkId: currentChunkId,
    });
  }

  getCurrentChunkId(): string {
    return this.getChunkContainerByIndex(this.currentChunkIndex).dataset.chunkId;
  }

  getCurrentChunkContainer(): ChunkContainerElement {
    return this.getChunkContainerByIndex(this.currentChunkIndex);
  }

  /**
   * Returns the current scroll position data for the current chunk.
   * This is used for emitting scroll events.
   */
  getCurrentScrollPositionData(): ChunkScrollPosition {
    const serializedPositionInfo = this.computeSerializedPositionFromCenteredElement();
    const scrollDepthWithinChunk = this.getScrollDepthWithinCurrentChunk();
    return {
      chunkIndex: this.currentChunkIndex,
      chunkId: this.getCurrentChunkId(),
      serializedPosition: serializedPositionInfo?.serializedPosition,
      serializedElementVerticalOffset: serializedPositionInfo?.serializedPositionElementOffset,
      scrollDepthWithinChunk,
    };
  }

  abstract scrollViewportToCurrentTTSLocation(rect: DOMRect): void;

  getScrollDepthWithinCurrentChunk(centeredElement?: HTMLElement | Range) {
    if (this._isScrolledToEndOfChunk()) {
      return 1;
    }

    const currentChunkElement = this.getChunkContainerByIndex(this.currentChunkIndex);

    return this.getChunkScrollDepth(currentChunkElement, centeredElement);
  }

  getScrollDataForTTSPosition(ttsPosition: TtsPosition): ChunkScrollPosition | undefined {
    const scrollableRoot = this.getScrollingElement();
    const contentContainer = this.getCurrentChunkContainer();
    if (!contentContainer || !scrollableRoot) {
      return;
    }
    const rect = getRectsFromTtsPosition({
      contentContainer,
      scrollableRoot,
      ttsPosition,
      returnRepeatedValue: true,
    })?.rect;
    if (!rect) {
      return;
    }

    const scrollDepthWithinChunk = this.getChunkScrollDepth(contentContainer, rect);
    return { scrollDepthWithinChunk, chunkIndex: this.currentChunkIndex };
  }

  async scrollToReadingPositionScrollDepth(totalScrollDepthOfDocument: number) {
    const allWebviewChunks = await getAllWebviewDocumentChunks();
    if (!allWebviewChunks) {
      return;
    }
    const {
      chunkIndex,
      scrollDepthWithinChunk,
    } = calculateRelativeChunkScrollDepthFromTotalScrollDepth(allWebviewChunks, totalScrollDepthOfDocument);
    return this._scrollToScrollDepthPercentOfChunk(chunkIndex, scrollDepthWithinChunk);
  }

  async scrollToTtsPosition(chunkId: string, ttsPosition: TtsPosition, skipIndicatorUpdate: boolean) {
    const chunkContainer = getChunkContainerByChunkId(chunkId);
    if (!chunkContainer) {
      throw new ScrollingManagerError(`ScrollToTTS: Failed to find chunk with id ${chunkId}`);
    }
    await this.setNewChunkIndex(Number(chunkContainer.dataset.chunkIndex));
    const scrollableRoot = this.getScrollingElement();
    const contentContainer = this.getCurrentChunkContainer();
    if (!contentContainer || !scrollableRoot) {
      throw new ScrollingManagerError(`ScrollToTTS: no content container or scrollable root`);
    }

    // If TTS is not on, neither is auto-scrolling.
    // If that's the case, we want to briefly re-enable it to get to the position.
    const isScrollingManagerAutoScrollEnabled = this.ttsAutoScrollingEnabled;
    this.ttsAutoScrollingEnabled = true;
    await scrollToTtsPosition({
      contentContainer,
      scrollableRoot,
      ttsPosition,
      isAutoScrollEnabled: true,
      skipIndicatorUpdate,
      useRepeatedLastWord: true,
    });
    this.ttsAutoScrollingEnabled = isScrollingManagerAutoScrollEnabled;
    return true;
  }

  computeSerializedPositionFromCenteredElement(): SerializedPositionInfo | undefined {
    const currentCenteredElementInfo = this.currentCenteredElementInfo;
    if (!currentCenteredElementInfo) {
      throw new Error('computeSerializedPositionFromCenteredElement: No center element info found');
    }
    const { element, scrollDelta } = currentCenteredElementInfo;
    if (!element || scrollDelta === undefined) {
      return;
    }
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('computeSerializedPositionFromCenteredElement: No document text content found');
    }
    if (!findChunkContainerForNode(element, this.documentTextContent)) {
      exceptionHandler.captureException('computeSerializedPositionFromCenteredElement: Element has no chunk container associated, probably outdated');
      return;
    }
    const serializedPosition = serializePositionAsCanonical({
      classApplier: getRangyClassApplier(),
      node: element,
      offset: 0,
      rootNode: this.documentTextContent,
    });

    return {
      serializedPosition,
      serializedPositionElementOffset: scrollDelta,
    };
  }

  createHighlightAtTtsPosition(ttsPos: TtsPosition) {
    const currentChunkContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    createHighlightAtTtsPosition(currentChunkContainer, ttsPos);
  }

  scrollToTop() {
    this.setScrollingElementTop(0);
  }

  /**
   * Scrolls to a chunk by its ID.
   * Returns true if successfully scrolled, false if chunk not found.
   */
  /**
   * Scrolls to the start of a chunk by its ID.
   * Throws if the chunk is not found or scrolling fails.
   */
  async scrollToChunkStart(chunkId: string): Promise<void> {
    const chunkContainer = getChunkContainerByChunkId(chunkId);
    if (!chunkContainer) {
      throw new ScrollingManagerError(`Chunk not found: ${chunkId}`);
    }
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('Document text content not found');
    }

    const serializedPosition = serializePositionAsCanonical({
      classApplier: getRangyClassApplier(),
      node: chunkContainer,
      offset: 0,
      rootNode: this.documentTextContent,
    });

    await this.scrollToSerializedPosition(serializedPosition, 0, 'instant');
  }

  /**
   * Gets the best EPUB start chunk ID using a three-tier priority:
   * 1. bodymatter landmark (canonical start)
   * 2. First page_list entry with valid page number (roman or integer)
   * 3. First front matter landmark (dedication through preamble)
   * Returns the chunk ID or undefined if no suitable position found.
   * Result is memoized.
   */
  getEpubStartChunkId(
    landmarks?: EpubLandmark[],
    pageList?: EpubPageListItem[],
  ): string | undefined {
    // Return memoized value if available
    if (this._cachedStartChunkId !== undefined) {
      return this._cachedStartChunkId;
    }

    // Front matter landmark types (stop after preamble - don't jump to part/chapter)
    const frontMatterTypes = new Set([
      'dedication',
      'epigraph',
      'foreword',
      'preface',
      'introduction',
      'prologue',
      'preamble',
    ]);

    // Priority 1: bodymatter landmark (canonical start of main content)
    const bodymatter = landmarks?.find((l) => l.landmark_type === 'bodymatter');
    if (bodymatter?.chunk_id) {
      this._cachedStartChunkId = bodymatter.chunk_id;
      return this._cachedStartChunkId;
    }

    // Priority 2: first page_list entry with valid page number (roman or integer)
    if (pageList?.length) {
      const firstValidPage = pageList.find(
        (p) => p.chunk_id && isValidPageNumber(p.page_number),
      );
      if (firstValidPage?.chunk_id) {
        this._cachedStartChunkId = firstValidPage.chunk_id;
        return this._cachedStartChunkId;
      }
    }

    // Priority 3: first front matter landmark (sorted by chunk order)
    if (landmarks?.length) {
      // Filter to front matter landmarks that have chunks existing in the DOM
      const frontMatterLandmarks = landmarks
        .filter((l) => l.chunk_id && frontMatterTypes.has(l.landmark_type))
        .map((l) => ({ landmark: l, chunkContainer: getChunkContainerByChunkId(l.chunk_id!) }))
        .filter((item) => item.chunkContainer !== null);

      if (frontMatterLandmarks.length) {
        // Sort by chunk order using DOM position
        const sorted = frontMatterLandmarks.sort((a, b) => {
          return Number(a.chunkContainer!.dataset.chunkIndex) - Number(b.chunkContainer!.dataset.chunkIndex);
        });

        this._cachedStartChunkId = sorted[0].landmark.chunk_id!;
        return this._cachedStartChunkId;
      }
    }

    // No valid start position found
    return undefined;
  }

  /**
   * Gets the best EPUB end chunk ID using a three-tier priority:
   * 1. backmatter landmark (canonical end per EPUB 3.3 spec)
   * 2. Last page_list entry with valid page number (highest numbered page)
   * 3. Last end matter landmark (epilogue, afterword, conclusion, etc.)
   * 4. Fallback to the last chunk in the document
   * Returns the chunk ID. Result is memoized.
   */
  getEpubEndChunkId(
    landmarks?: EpubLandmark[],
    pageList?: EpubPageListItem[],
  ): string | undefined {
    // Return memoized value if available
    if (this._cachedEndChunkId !== undefined) {
      return this._cachedEndChunkId;
    }

    // End matter landmark types (excludes reference sections like appendix/glossary/bibliography/index
    // that users might access throughout reading)
    const endMatterTypes = new Set([
      'epilogue',
      'afterword',
      'conclusion',
      'colophon',
    ]);

    // Priority 1: backmatter landmark (canonical end of main content per EPUB 3.3)
    const backmatter = landmarks?.find((l) => l.landmark_type === 'backmatter');
    if (backmatter?.chunk_id) {
      this._cachedEndChunkId = backmatter.chunk_id;
      return this._cachedEndChunkId;
    }

    // Priority 2: last page_list entry with valid page number
    if (pageList?.length) {
      // Find all entries with valid page numbers and get the last one
      const validPages = pageList.filter(
        (p) => p.chunk_id && isValidPageNumber(p.page_number),
      );
      if (validPages.length) {
        const lastValidPage = validPages[validPages.length - 1];
        this._cachedEndChunkId = lastValidPage.chunk_id!;
        return this._cachedEndChunkId;
      }
    }

    // Priority 3: last end matter landmark (sorted by chunk order, pick last)
    if (landmarks?.length) {
      const endMatterLandmarks = landmarks
        .filter((l) => l.chunk_id && endMatterTypes.has(l.landmark_type))
        .map((l) => ({ landmark: l, chunkContainer: getChunkContainerByChunkId(l.chunk_id!) }))
        .filter((item) => item.chunkContainer !== null);

      if (endMatterLandmarks.length) {
        // Sort by chunk order and pick the last one
        const sorted = endMatterLandmarks.sort((a, b) => {
          return Number(a.chunkContainer!.dataset.chunkIndex) - Number(b.chunkContainer!.dataset.chunkIndex);
        });

        this._cachedEndChunkId = sorted[sorted.length - 1].landmark.chunk_id!;
        return this._cachedEndChunkId;
      }
    }

    // Priority 4: fallback to the last chunk in the document
    const chunkContainers = getChunkContainerElements();
    if (chunkContainers.length) {
      const lastChunk = chunkContainers[chunkContainers.length - 1];
      this._cachedEndChunkId = lastChunk.dataset.chunkId;
      return this._cachedEndChunkId;
    }

    return undefined;
  }

  /**
   * Scrolls to the best EPUB start position.
   * Throws if no suitable start position is found or if scrolling fails.
   */
  async scrollToEpubStartPosition(
    landmarks?: EpubLandmark[],
    pageList?: EpubPageListItem[],
  ): Promise<void> {
    const startChunkId = this.getEpubStartChunkId(landmarks, pageList);
    if (!startChunkId) {
      throw new ScrollingManagerError('No EPUB start position found');
    }
    await this.scrollToChunkStart(startChunkId);
  }

  abstract scrollToStartOfDocument(landmarks?: EpubLandmark[], pageList?: EpubPageListItem[]): Promise<void>;

  abstract returnToReadingPosition(): Promise<void>;

  abstract onScrollStart(): void;

  // This is unthrottled, only add code to this if you need to listen to scroll a lot (like animating elements due to scroll position)
  abstract onScroll(): void;

  abstract onScrollEnd(): void;

  abstract onTouchMove(e: TouchEvent): void;

  onContentChanged() {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('onContentChanged Document Text Container not found');
    }
    this.focusableElements = [];
    this._internalDocumentTextRanges = undefined;
    const currentChunkContainer = this.getChunkContainerByIndex(this.currentChunkIndex);
    populateOrRepopulateTTSAbleElements(currentChunkContainer);
    logger.debug('onContentChanged', { highlightableElements: this.focusableElements });
  }

  toggleScrollingEnabled(enabled: boolean) {
    if (!enabled) {
      if (this.scrollingEnabled) {
        this.document.body.style.overflow = 'hidden';
        this.window.addEventListener('touchmove', preventDefault, false); // mobile
      }
      this.scrollingEnabled = false;
    } else {
      this.window.removeEventListener('touchmove', preventDefault, false);
      this.document.body.style.overflow = 'visible';
      this.scrollingEnabled = true;
    }
  }

  // Util function to help move a debug border to a coordinate, useful for visualizing coordinates
  drawDebugBorderAtCoordY(coordY: number) {
    const border = this.document.querySelector<HTMLElement>('.debug-border');
    if (!border) {
      return;
    }
    border.style.top = `${coordY}px`;
    border.style.display = 'block';
  }

  drawDebugBorderAtCoordX(coordX: number) {
    const border = this.document.querySelector<HTMLElement>('.debug-border-x');
    if (!border) {
      return;
    }
    border.style.left = `${coordX}px`;
    border.style.top = `${0}px`;
    border.style.display = 'block';
  }

  drawDebugBorderAtXY(coordX: number, coordY: number) {
    this.drawDebugBorderAtCoordX(coordX);
    this.drawDebugBorderAtCoordY(coordY);
  }

  isElementIntersectingAtYCoord(element: Element, yCoord: number) {
    if (!this.documentRoot) {
      throw new ScrollingManagerError('No document root found');
    }
    const relativeYCoord = this.documentRoot.getBoundingClientRect().top + yCoord;
    return isIntersecting(
      {
        top: relativeYCoord,
        bottom: relativeYCoord + 1,
        left: 0,
        right: this.window.innerWidth,
      },
      element,
    );
  }

  get numberOfChunks(): number {
    return getChunkContainerElements().length;
  }

  get isCurrentChunkIndexFinal(): boolean {
    return this.currentChunkIndex === this.numberOfChunks - 1;
  }

  get documentTextContentRanges(): Range[] {
    if (!this.initialized) {
      return [];
    }
    if (this._internalDocumentTextRanges?.length) {
      return this._internalDocumentTextRanges;
    }
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('No document text content found');
    }

    const shouldUseSecondLevelChildren = (children: HTMLCollection) => {
      if (children.length !== 1) {
        return false;
      }
      const firstChild = children[0];
      if (firstChild.nodeName !== 'DIV') {
        return false;
      }
      // If a div has some padding or margin added, chances are that class might affect the look of the content
      // best to use this div even if we will have large ranges in our set
      const firstChildComputedMap = window.getComputedStyle(firstChild);
      const marginStyles = firstChildComputedMap.margin?.toString() ?? '0px';
      const paddingStyles = firstChildComputedMap.padding?.toString() ?? '0px';
      return marginStyles === '0px' && paddingStyles === '0px';
    };

    const childrenToUse: Range[] = [];
    const chunkContainers = this.documentTextContent.querySelectorAll('.rw-chunk-container');
    for (const chunkContainer of chunkContainers) {
      let topLevelDocumentChildren = Array.from(chunkContainer.childNodes);
      // if we only have one main element, and that element has children, most likely that's the element we are interested in
      if (shouldUseSecondLevelChildren(chunkContainer.children)) {
        topLevelDocumentChildren = Array.from(chunkContainer.children[0].childNodes)
          .filter((node) => !IGNORED_DOCUMENT_CHILD_NODE_TAGS.has(node.nodeName));
      }
      for (const child of topLevelDocumentChildren) {
        if (isNodeAnHTMLElement(child)) {
          const range = new Range();
          if (child.getBoundingClientRect().height > 0 || child.getBoundingClientRect().width > 0) {
            range.selectNode(child);
            childrenToUse.push(range);
          }
        } else if (isTextNode(child)) {
          const range = new Range();
          range.selectNode(child);
          if (range.getClientRects().length > 0) {
            childrenToUse.push(range);
          }
        }
      }
    }
    this._internalDocumentTextRanges = childrenToUse;
    return childrenToUse;
  }

  doesElementContainDirectTextNodes(element: Element) {
    for (const child of element.childNodes) {
      if (isTextNode(child) && child.textContent && child.textContent.trim().length >= 1) {
        return true;
      }
    }
    return false;
  }

  getEfficientClientRectFromRange(range: Range) {
    const firstElement = this.getFirstNodeInRange(range);
    if (firstElement instanceof Element) {
      return firstElement.getBoundingClientRect();
    }
    return range.getBoundingClientRect();
  }

  getRangeAtY(yCoord: number, returnClosestElement = false): Range | null {
    if (!this.documentRoot) {
      throw new ScrollingManagerError('No document root found');
    }
    const allRanges = this.documentTextContentRanges;
    let left = 0;
    let right = allRanges.length - 1;
    let middleRange = null;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      middleRange = allRanges[mid];
      const clientRect = this.getEfficientClientRectFromRange(middleRange);

      const absoluteTopOfElement =
        clientRect.top + this.getScrollingElementTop();
      const absoluteBottomOfElement =
        clientRect.bottom + this.getScrollingElementTop();

      if (absoluteTopOfElement <= yCoord && absoluteBottomOfElement >= yCoord) {
        return middleRange;
      }
      if (absoluteBottomOfElement < yCoord) {
        // this range is above the ycoord
        left = mid + 1;
      } else if (absoluteTopOfElement > yCoord) {
        // this range is below the ycoord
        right = mid - 1;
      } else {
        // this else case makes no sense
        // for us to arrive here the element must
        // have a bottom coord below the ycoord and a top above ycoord
        // but it didn't intersect... ?
        // In any case I wouldn't want the loop to run forever so returning null here is ok
        return null;
      }
    }
    // If we got here that means we didnt get a super precise value but
    // we can still return the last seen middle range in our search
    if (middleRange && returnClosestElement) {
      return middleRange;
    }
    return null;
  }

  getAllRangesInRect(pageRect: PageRect, verticalMargin: number): Range[] {
    const viewportWidth = this.window.innerWidth;
    const relativePageRect = {
      top: pageRect.top - this.getScrollingElementTop() - verticalMargin,
      bottom: pageRect.bottom - this.getScrollingElementTop() + verticalMargin,
      left: 0,
      right: viewportWidth,
    };
    return getVisibleElementsWithinRectBounds(this.documentTextContentRanges, relativePageRect);
  }

  getTextContentFromElementOrRange(elementOrRange: Element | Range) {
    let textContent = '';
    if (elementOrRange instanceof Element) {
      textContent = elementOrRange.textContent ?? '';
    }
    if (elementOrRange instanceof Range) {
      textContent = elementOrRange.toString();
    }
    return textContent;
  }

  getFirstNodeInRange(range: Range): Node {
    return range.startContainer.childNodes[range.startOffset] ?? range.startContainer;
  }

  getNumberOfWordsInRect(rect: PageRect) {
    const visibleRanges = this.getAllRangesInRect(rect, 0);
    if (!visibleRanges.length) {
      return 0;
    }

    const firstRange = visibleRanges[0];
    const lastRange = visibleRanges[visibleRanges.length - 1];

    let totalWordCount = 0;

    // First we need to see if the firstRange gets cut-off at the top
    const scrollTopOfFirstElement = this.getAbsoluteScrollTopOfRange(firstRange);
    const firstElementHeight = firstRange.getBoundingClientRect().height;
    // We want to see what % of the element is visible within the provided rect
    // (e.g it might be half above and half inside the rect)
    const firstElementDistanceFromRectTop = rect.top - scrollTopOfFirstElement;
    let percentOfFirstElementVisible = 1;
    if (firstElementDistanceFromRectTop > 0) {
      // the first element is cut-off, figure out by how much
      const visibleHeight = Math.min(Math.max(0, firstElementHeight - firstElementDistanceFromRectTop), this.pageHeight);
      percentOfFirstElementVisible = Math.min(1, visibleHeight / firstElementHeight);
    }

    const firstElementTextContent = this.getTextContentFromElementOrRange(firstRange);

    totalWordCount += firstElementTextContent.split(' ').length * percentOfFirstElementVisible;

    for (let i = 1; i < visibleRanges.length - 1; i++) {
      const textContent = this.getTextContentFromElementOrRange(visibleRanges[i]);
      totalWordCount += textContent.split(' ').length;
    }


    if (lastRange === firstRange) {
      return Math.round(totalWordCount);
    }
    // Now we do the same with the last element

    const scrollTopOfLastElement = this.getAbsoluteScrollTopOfRange(lastRange);
    const lastElementHeight = lastRange.getBoundingClientRect().height;
    let percentOfLastElementVisible = 1;
    const lastElementDistanceFromBottom = rect.bottom - Math.max(scrollTopOfLastElement, this.getScrollingElementTop());
    if (lastElementDistanceFromBottom < 0) {
      percentOfLastElementVisible = 0;
    } else {
      percentOfLastElementVisible = Math.min(1, lastElementDistanceFromBottom / lastElementHeight);
    }

    const lastElementTextContent = this.getTextContentFromElementOrRange(lastRange);
    totalWordCount += lastElementTextContent.split(' ').length * percentOfLastElementVisible;
    return Math.round(totalWordCount);
  }

  getNumberOfWordsOnScreen() {
    if (!this.documentTextContent) {
      return 0;
    }
    const rect = {
      top: this.getScrollingElementTop(),
      bottom: this.getScrollingElementTop() + this.window.innerHeight,
      bottomPageDividerHeight: 0,
    };
    return Math.min(2000, this.getNumberOfWordsInRect(rect));
  }


  setSpinnerEnabled(enabled: boolean, instant = false) {
    const spinner = this.document.querySelector('.chunk-loading-spinner');
    if (!spinner) {
      return;
    }
    if (this.spinnerTimeout && enabled) {
      // we dont need to start it again, we already triggered it
      return;
    }
    if (!enabled) {
      if (this.spinnerTimeout) {
        clearTimeout(this.spinnerTimeout);
        this.spinnerTimeout = undefined;
      }
      spinner.classList.remove('chunk-loading-spinner-active');
      return;
    }
    if (!enabled && this.spinnerTimeout) {
      clearTimeout(this.spinnerTimeout);
    }
    this.spinnerTimeout = setTimeout(() => {
      const spinner = this.document.querySelector('.chunk-loading-spinner');
      if (!spinner) {
        return;
      }
      if (enabled) {
        spinner.classList.add('chunk-loading-spinner-active');
      }
    }, instant ? 1 : 1000);
  }

  async scrollToLinkElement(elementReference: string, sourceLinkElement: HTMLElement) {
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('No document text content found');
    }

    const scrollTarget = await getScrollTargetForInternalLink(
      elementReference,
      this.documentTextContent,
      this._loadOnlyContentSurroundingChunkContainer.bind(this),
      sourceLinkElement,
    );

    if (!scrollTarget) {
      exceptionHandler.captureException('scrollToLinkElement: No scroll target found', { extra: { elementReference } });
      return;
    }

    return this._scrollToRangeOrElement(scrollTarget);
  }

  async scrollToHeadingWithId(elementReference: string, isEpub: boolean) {
    this.window.portalGateToForeground.emit('set_tts_auto_scrolling_enabled', false);
    if (!this.documentTextContent) {
      throw new ScrollingManagerError('No document text content found');
    }
    const scrollTarget = await getHeadingElement(
      elementReference,
      isEpub,
      this.documentTextContent,
      this._loadOnlyContentSurroundingChunkContainer.bind(this),
    );

    if (!scrollTarget) {
      exceptionHandler.captureException('scrollToHeadingWithId: No scroll target found', {
        extra: {
          elementReference,
          isEpub,
        },
      });
      return;
    }
    return this._scrollToRangeOrElement(scrollTarget);
  }

  toggleSmoothAnimations(disabled: boolean) {
    this.smoothAnimationsDisabled = disabled;
    if (disabled) {
      this.document.body.classList.add('disable-scroll-due-to-reduce-animations');
    } else {
      this.document.body.classList.remove('disable-scroll-due-to-reduce-animations');
    }
  }

  // return true if an element should not allow a scroll event to happen if it is clicked on a margin
  isTouchTargetClickableOnMargins(touchTarget: Node | undefined | null) {
    if (!isHTMLElement(touchTarget)) {
      return false;
    }
    return (
      touchTarget.closest('a, rw-highlight') !== null ||
      touchTarget.classList.contains('tts-button-text')
    );
  }

  logFPS() {
    this.shouldLogFPS = true;
    this.debugTrackFPS();
  }

  debugTrackFPS() {
    if (!this.shouldLogFPS) {
      return;
    }
    const logger = makeLogger('debugTrackFPS', {
      shouldLog: this.shouldLogFPS,
    });
    if (!this.fpsTrackingStartTime) {
      this.fpsTrackingStartTime = nowTimestamp();
    }
    if (!this.fpsStart) {
      this.fpsStart = nowTimestamp();
    }
    requestAnimationFrame(() => {
      if (this.fpsCounter > 20 && !this.hasFPSReachedThreshold) {
        logger.log('We are usable ', nowTimestamp() - this.fpsTrackingStartTime);
        this.hasFPSReachedThreshold = true;
      }
      if (nowTimestamp() - this.fpsStart >= 1000) {
        logger.log('fps: ', this.fpsCounter);
        this.fpsCounter = 0;
        this.fpsStart = nowTimestamp();
      }
      this.fpsCounter += 1;
      this.debugTrackFPS();
    });
  }

  abstract _isScrolledToEndOfChunk(): boolean;

  abstract _scrollToRangeOrElement(element: HTMLElement | Range, offset?: number, behavior?: 'smooth' | 'auto' | 'instant'): Promise<void>;

  abstract _scrollToScrollDepthPercentOfChunk(chunkIndex: number, scrollDepthWithinChunk: number): Promise<void>;

  protected async _loadOnlyContentSurroundingChunkContainer(container: ChunkContainerElement): Promise<void> {
    // If we have a reading position, keep that container loaded
    const containerWhichContainsReadingPosition: string[] = [];
    if (this.readingPosition?.serializedPosition) {
      // note on performance: usually a no-op since this should be loaded already
      const chunk = await this.loadChunkContentAtSerializedPosition(this.readingPosition?.serializedPosition);
      if (chunk) {
        containerWhichContainsReadingPosition.push(chunk.dataset.chunkId);
      }
    }
    const allPromises = getSurroundingChunkPromisesToLoadAndUnload(container, containerWhichContainsReadingPosition);
    if (allPromises.length === 0) {
      return;
    }
    this.incrementChunksLoading();
    await Promise.all(allPromises);
    // console.log('Done loading surrounding chunks');
    this.decrementChunksLoading();
  }

  protected _disableScrollingWithTouch() {
    this.logger.log('Disable scroll with touch');
    if (this.debugFreeScroll) {
      return;
    }
    this.document.body.classList.add('disable-scroll');
    if (this.enableScrollTimeout) {
      clearTimeout(this.enableScrollTimeout);
    }
  }

  protected _enableScrollingWithTouch() {
    if (this.enableScrollTimeout) {
      clearTimeout(this.enableScrollTimeout);
    }
    this.logger.log('We want to re-enable scroll with touch in a sec');
    this.enableScrollTimeout = setTimeout(() => {
      this.document.body.classList.remove('disable-scroll');
      this.logger.log('enable scroll with touch');
    }, 10);
  }

}
